<script lang="ts">
	import { appStore } from '../stores/appStore';
	import { getBrowserInfo } from '../utils/browserDetection';
	import { getTokenStorage, setTokenStorage } from '../utils/localStorage';
	import type { AppApiClient } from '../api_contracts/app.client';
	import Preloader from '../components/auth/Preloader.svelte';
	import DarkModeToggle from '../components/auth/DarkModeToggle.svelte';
	import BrandingSide from '../components/auth/BrandingSide.svelte';
	import BackToDashboard from '../components/auth/BackToDashboard.svelte';

	let client: AppApiClient | null = $state(null);
	let email = $state('');
	let password = $state('');
	let rememberMe = $state(false);
	let loading = $state(false);
	let error = $state('');
	let showPassword = $state(false);

	// 2FA state
	let show2FAPrompt = $state(false);
	let twoFactorCode = $state('');
	let tempToken = $state('');

	// Check if user is already logged in (reactive to client changes)
	$effect(() => {
		// Make this effect reactive to client changes
		if (client) {
			const token = getTokenStorage();
			if (token) {
				(async () => {
					try {
						const result = await client.validateToken({ token });
						console.log('Token from localStorage:', token);
						if (result.response.successFull) {
							// Redirect to already logged in page
							window.location.hash = '/already-logged-in';
						} else {
							console.log('Token is invalid.');
							setTokenStorage(undefined);
							appStore.setToken('');
							// Stay on login page
						}
					} catch (e) {
						console.error('Error validating token:', e);
					}
				})();
			}
		}
	});

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
	});

	async function getIPInfo() {
		try {
			// Get IP address
			const ipResponse = await fetch('https://api.ipify.org?format=json');
			const ipData = await ipResponse.json();

			// Get location info (optional)
			const infoResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
			const infoData = await infoResponse.json();

			return {
				ip: ipData.ip,
				city: infoData.city,
				region: infoData.region,
				country: infoData.country_name,
				isp: infoData.org,
				timezone: infoData.timezone
			};
		} catch (error) {
			console.error('Error:', error);
			return null;
		}
	}

	async function handleLogin(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		if (!email || !password) {
			error = 'Please fill in all fields';
			return;
		}

		try {
			loading = true;
			error = '';
			let location = 'Unknown..';
			let userIp = 'Unknown..';
			let locationAndIp = await getIPInfo();

			if (locationAndIp) {
				location = `${locationAndIp.city}, ${locationAndIp.country}.`;
				userIp = locationAndIp.ip;
			}
			const browserInfo = getBrowserInfo();

			const result = await client.login({
				browserName: browserInfo.browser,
				osName: browserInfo.os,
				userIp,
				location,
				email,
				password
			});

			if (result.response.successFull) {
				// Check if 2FA is required
				if (result.response.requires2FA) {
					tempToken = result.response.tempToken || '';
					show2FAPrompt = true;
					loading = false;
					return;
				}

				// Store token
				const token = result.response.token;
				appStore.setToken(token);
				setTokenStorage(token);

				// Fetch user profile before redirecting
				try {
					const profileResult = await client.getUserProfile({ token });
					if (profileResult.response.response.successFull && profileResult.response.profile) {
						appStore.setProfile(profileResult.response.profile);
						console.log('User profile fetched and stored after login.');
					}
				} catch (e) {
					console.error('Error fetching profile after login:', e);
				}

				// Redirect to home or dashboard
				window.location.hash = '/';
			} else {
				error = result.response.reason || 'Login failed';

				// Check if error is about email verification
				if (result.response.reason?.includes('verify your email')) {
					// Store email for verification page
					localStorage.setItem('pendingVerificationEmail', email);
					// Show link to verification page
					setTimeout(() => {
						if (confirm('Your email is not verified. Would you like to verify it now?')) {
							window.location.hash = '/verify-email';
						}
					}, 500);
				}
			}
		} catch (e: any) {
			error = e.message || 'Error during login';
			console.error('Login error:', e);
		} finally {
			loading = false;
		}
	}

	async function handleVerifyLogin2FA(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		if (!twoFactorCode.trim()) {
			error = 'Please enter the 2FA code';
			return;
		}

		// Accept both 6-digit TOTP codes and 8-character backup codes
		if (twoFactorCode.length < 6 || twoFactorCode.length > 8) {
			error = 'Please enter a valid 6-digit code or 8-character backup code';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.verifyLogin2FA({
				tempToken,
				code: twoFactorCode
			});

			if (result.response.successFull) {
				// Store token
				const token = result.response.token || '';
				appStore.setToken(token);
				setTokenStorage(token);

				// Fetch user profile before redirecting
				try {
					const profileResult = await client.getUserProfile({ token });
					if (profileResult.response.response.successFull && profileResult.response.profile) {
						appStore.setProfile(profileResult.response.profile);
						console.log('User profile fetched and stored after 2FA login.');
					}
				} catch (e) {
					console.error('Error fetching profile after 2FA login:', e);
				}

				// Redirect to home or dashboard
				window.location.hash = '/';
			} else {
				error = result.response.reason || 'Invalid 2FA code';
			}
		} catch (e: any) {
			error = e.message || 'Error during 2FA verification';
			console.error('2FA verification error:', e);
		} finally {
			loading = false;
		}
	}

	async function handleGoogleLogin() {
		if (!client) {
			error = 'Client not initialized';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.getGoogleOAuthURL({
				redirectUrl: window.location.origin + '/#/auth/google/callback'
			});

			if (result.response.successFull && result.response.authUrl) {
				// Store state token for CSRF protection
				localStorage.setItem('oauth_state', result.response.state);
				// Redirect to Google OAuth
				window.location.href = result.response.authUrl;
			} else {
				error = result.response.reason || 'Failed to initiate Google login';
				loading = false;
			}
		} catch (e: any) {
			error = e.message || 'Error initiating Google login';
			console.error('Google login error:', e);
			loading = false;
		}
	}

	async function handleGitHubLogin() {
		if (!client) {
			error = 'Client not initialized';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.getGitHubOAuthURL({
				redirectUrl: window.location.origin + '/#/auth/github/callback'
			});

			if (result.response.successFull && result.response.authUrl) {
				// Store state token for CSRF protection
				localStorage.setItem('oauth_state', result.response.state);
				// Redirect to GitHub OAuth
				window.location.href = result.response.authUrl;
			} else {
				error = result.response.reason || 'Failed to initiate GitHub login';
				loading = false;
			}
		} catch (e: any) {
			error = e.message || 'Error initiating GitHub login';
			console.error('GitHub login error:', e);
			loading = false;
		}
	}
</script>

<Preloader />
<DarkModeToggle />

<div class="relative z-1 bg-white p-6 sm:p-0 dark:bg-gray-900">
	<div
		class="relative flex h-screen w-full flex-col justify-center sm:p-0 lg:flex-row dark:bg-gray-900"
	>
		<!-- Left Side: Form -->
		<div class="flex w-full flex-1 flex-col lg:w-1/2">
			<div class="mx-auto w-full max-w-md pt-10">
				<BackToDashboard />
			</div>

			<div class="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
				{#if show2FAPrompt}
					<!-- 2FA Verification Form -->
					<div class="mb-5 sm:mb-8">
						<div class="flex items-center justify-center mb-4">
							<svg
								class="w-16 h-16 text-brand-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
								/>
							</svg>
						</div>
						<h1
							class="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 text-center dark:text-white/90"
						>
							Two-Factor Authentication
						</h1>
						<p class="text-sm text-gray-500 text-center dark:text-gray-400">
							Enter the 6-digit code from your authenticator app
						</p>
					</div>

					<div>
						<form onsubmit={handleVerifyLogin2FA}>
							{#if error}
								<div
									class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800"
								>
									<p class="text-sm text-red-600 dark:text-red-400">{error}</p>
								</div>
							{/if}

							<div class="space-y-5">
								<div>
									<label
										for="twoFactorCode"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Authentication Code<span class="text-error-500">*</span>
									</label>
									<input
										type="text"
										id="twoFactorCode"
										bind:value={twoFactorCode}
										disabled={loading}
										maxlength="8"
										inputmode="numeric"
										placeholder="000000"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 text-center text-2xl tracking-widest"
										required
										autofocus
									/>
									<p class="text-xs text-gray-500 mt-1.5 dark:text-gray-400">
										You can also use a backup code if you don't have access to your authenticator
										app
									</p>
								</div>

								<div>
									<button
										type="submit"
										disabled={loading}
										class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										{loading ? 'Verifying...' : 'Verify Code'}
									</button>
								</div>

								<div>
									<button
										type="button"
										onclick={() => {
											show2FAPrompt = false;
											twoFactorCode = '';
											tempToken = '';
											error = '';
										}}
										class="w-full text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
									>
										Back to login
									</button>
								</div>
							</div>
						</form>
					</div>
				{:else}
					<!-- Regular Login Form -->
					<div class="mb-5 sm:mb-8">
						<h1
							class="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 dark:text-white/90"
						>
							Sign In
						</h1>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							Enter your email and password to sign in!
						</p>
					</div>

					<div>
						<!-- Social Auth Buttons -->
						<div class="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5">
							<button
								type="button"
								onclick={handleGoogleLogin}
								disabled={loading}
								class="inline-flex items-center justify-center gap-3 rounded-lg bg-gray-100 px-7 py-3 text-sm font-normal text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<!-- Google Icon -->
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z" fill="#4285F4"/>
									<path d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z" fill="#34A853"/>
									<path d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z" fill="#FBBC05"/>
									<path d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z" fill="#EB4335"/>
								</svg>
								Sign in with Google
							</button>
							<button
								type="button"
								onclick={handleGitHubLogin}
								disabled={loading}
								class="inline-flex items-center justify-center gap-3 rounded-lg bg-gray-100 px-7 py-3 text-sm font-normal text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<!-- GitHub Icon -->
								<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
									<path fill-rule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clip-rule="evenodd"/>
								</svg>
								Sign in with GitHub
							</button>
						</div>

						<!-- Divider -->
						<div class="relative py-3 sm:py-5">
							<div class="absolute inset-0 flex items-center">
								<div class="w-full border-t border-gray-200 dark:border-gray-800"></div>
							</div>
							<div class="relative flex justify-center text-sm">
								<span class="bg-white p-2 text-gray-400 sm:px-5 sm:py-2 dark:bg-gray-900">Or</span>
							</div>
						</div>

						<!-- Form -->
						<form onsubmit={handleLogin}>
							{#if error}
								<div
									class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800"
								>
									<p class="text-sm text-red-600 dark:text-red-400">{error}</p>
								</div>
							{/if}

							<div class="space-y-5">
								<!-- Email -->
								<div>
									<label
										for="email"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Email<span class="text-error-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										name="email"
										bind:value={email}
										disabled={loading}
										placeholder="info@gmail.com"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
										required
									/>
								</div>

								<!-- Password -->
								<div>
									<label
										for="password"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Password<span class="text-error-500">*</span>
									</label>
									<div class="relative">
										<input
											type={showPassword ? 'text' : 'password'}
											id="password"
											name="password"
											bind:value={password}
											disabled={loading}
											placeholder="Enter your password"
											class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent py-2.5 pr-11 pl-4 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
											required
										/>
										<span
											onclick={() => (showPassword = !showPassword)}
											class="absolute top-1/2 right-4 z-30 -translate-y-1/2 cursor-pointer text-gray-500 dark:text-gray-400"
										>
											{#if showPassword}
												<!-- Eye Slash -->
												<svg
													class="fill-current"
													width="20"
													height="20"
													viewBox="0 0 20 20"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M4.63803 3.57709C4.34513 3.2842 3.87026 3.2842 3.57737 3.57709C3.28447 3.86999 3.28447 4.34486 3.57737 4.63775L4.85323 5.91362C3.74609 6.84199 2.89363 8.06395 2.4155 9.45936C2.3615 9.61694 2.3615 9.78801 2.41549 9.94558C3.49488 13.0957 6.48191 15.3619 10.0002 15.3619C11.255 15.3619 12.4422 15.0737 13.4994 14.5598L15.3625 16.4229C15.6554 16.7158 16.1302 16.7158 16.4231 16.4229C16.716 16.13 16.716 15.6551 16.4231 15.3622L4.63803 3.57709ZM12.3608 13.4212L10.4475 11.5079C10.3061 11.5423 10.1584 11.5606 10.0064 11.5606H9.99151C8.96527 11.5606 8.13333 10.7286 8.13333 9.70237C8.13333 9.5461 8.15262 9.39434 8.18895 9.24933L5.91885 6.97923C5.03505 7.69015 4.34057 8.62704 3.92328 9.70247C4.86803 12.1373 7.23361 13.8619 10.0002 13.8619C10.8326 13.8619 11.6287 13.7058 12.3608 13.4212ZM16.0771 9.70249C15.7843 10.4569 15.3552 11.1432 14.8199 11.7311L15.8813 12.7925C16.6329 11.9813 17.2187 11.0143 17.5849 9.94561C17.6389 9.78803 17.6389 9.61696 17.5849 9.45938C16.5055 6.30925 13.5184 4.04303 10.0002 4.04303C9.13525 4.04303 8.30244 4.17999 7.52218 4.43338L8.75139 5.66259C9.1556 5.58413 9.57311 5.54303 10.0002 5.54303C12.7667 5.54303 15.1323 7.26768 16.0771 9.70249Z"
														fill="#98A2B2"
													/>
												</svg>
											{:else}
												<!-- Eye -->
												<svg
													class="fill-current"
													width="20"
													height="20"
													viewBox="0 0 20 20"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M10.0002 13.8619C7.23361 13.8619 4.86803 12.1372 3.92328 9.70241C4.86804 7.26761 7.23361 5.54297 10.0002 5.54297C12.7667 5.54297 15.1323 7.26762 16.0771 9.70243C15.1323 12.1372 12.7667 13.8619 10.0002 13.8619ZM10.0002 4.04297C6.48191 4.04297 3.49489 6.30917 2.4155 9.4593C2.3615 9.61687 2.3615 9.78794 2.41549 9.94552C3.49488 13.0957 6.48191 15.3619 10.0002 15.3619C13.5184 15.3619 16.5055 13.0957 17.5849 9.94555C17.6389 9.78797 17.6389 9.6169 17.5849 9.45932C16.5055 6.30919 13.5184 4.04297 10.0002 4.04297ZM9.99151 7.84413C8.96527 7.84413 8.13333 8.67606 8.13333 9.70231C8.13333 10.7286 8.96527 11.5605 9.99151 11.5605H10.0064C11.0326 11.5605 11.8646 10.7286 11.8646 9.70231C11.8646 8.67606 11.0326 7.84413 10.0064 7.84413H9.99151Z"
														fill="#98A2B3"
													/>
												</svg>
											{/if}
										</span>
									</div>
								</div>

								<!-- Checkbox and Forgot Password -->
								<div class="flex items-center justify-between">
									<!-- Keep me logged in checkbox removed as it was commented out in original -->
									<div></div>
									<a
										href="#/forgot-password"
										class="text-brand-500 hover:text-brand-600 dark:text-brand-400 text-sm"
									>
										Forgot password?
									</a>
								</div>

								<!-- Button -->
								<div>
									<button
										type="submit"
										disabled={loading}
										class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										{loading ? 'Signing in...' : 'Sign In'}
									</button>
								</div>
							</div>
						</form>

						<div class="mt-5">
							<p
								class="text-center text-sm font-normal text-gray-700 sm:text-start dark:text-gray-400"
							>
								Don't have an account?
								<a href="#/register" class="text-brand-500 hover:text-brand-600 dark:text-brand-400"
									>Sign Up</a
								>
							</p>
						</div>
					</div>
				{/if}
			</div>
		</div>

		<!-- Right Side: Branding -->
		<BrandingSide />
	</div>
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
	}
</style>
